package com.tovtalk.ui.screen.settings

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.CameraAlt
import androidx.compose.material.icons.filled.ChatBubble
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.filled.VideoLibrary
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextFieldDefaults
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.di.disposableKoinInject
import com.tovtalk.domain.entity.Category
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.profile.ProfileViewModel
import com.tovtalk.ui.components.CenteredContentWrapper
import com.tovtalk.ui.screen.profile.EditProfileContactsDialog
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.launch

private enum class ContentSource { INSTAGRAM, WHATSAPP, YOUTUBE }

@Composable
fun ContentUploadManagementScreen(
    onNavigateBack: () -> Unit,
    viewModel: ProfileViewModel = disposableKoinInject(),
    appRepository: AppRepository = koinInject()
) {
    val userState by viewModel.userState.collectAsState()
    val updateProfileState by viewModel.updateProfileState.collectAsState()
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }

    // Source fields
    var instagramId by remember { mutableStateOf("") }
    var whatsappStatusId by remember { mutableStateOf("") }
    var youtubeChannelId by remember { mutableStateOf("") }
    var autoPullInstagram by remember { mutableStateOf(false) }
    var autoPullWhatsappStatus by remember { mutableStateOf(false) }
    var autoPullYoutube by remember { mutableStateOf(false) }
    var autoUpdateProfileInstagram by remember { mutableStateOf(false) }
    var autoUpdateProfileWhatsappStatus by remember { mutableStateOf(false) }
    var autoUpdateProfileYoutube by remember { mutableStateOf(false) }
    var uploadCategoryId by remember { mutableStateOf<Int?>(null) }

    // Original values for change detection
    var origInstagramId by remember { mutableStateOf("") }
    var origWhatsappStatusId by remember { mutableStateOf("") }
    var origYoutubeChannelId by remember { mutableStateOf("") }
    var origAutoPullInstagram by remember { mutableStateOf(false) }
    var origAutoPullWhatsappStatus by remember { mutableStateOf(false) }
    var origAutoPullYoutube by remember { mutableStateOf(false) }
    var origAutoUpdateProfileInstagram by remember { mutableStateOf(false) }
    var origAutoUpdateProfileWhatsappStatus by remember { mutableStateOf(false) }
    var origAutoUpdateProfileYoutube by remember { mutableStateOf(false) }
    var origUploadCategoryId by remember { mutableStateOf<Int?>(null) }

    // UI state
    var expandedSource by remember { mutableStateOf<ContentSource?>(null) }
    var showContactsDialog by remember { mutableStateOf(false) }
    var categories by remember { mutableStateOf<List<Category>>(emptyList()) }
    var categoryDropdownExpanded by remember { mutableStateOf(false) }

    val hasChanges = instagramId != origInstagramId ||
        whatsappStatusId != origWhatsappStatusId ||
        youtubeChannelId != origYoutubeChannelId ||
        autoPullInstagram != origAutoPullInstagram ||
        autoPullWhatsappStatus != origAutoPullWhatsappStatus ||
        autoPullYoutube != origAutoPullYoutube ||
        autoUpdateProfileInstagram != origAutoUpdateProfileInstagram ||
        autoUpdateProfileWhatsappStatus != origAutoUpdateProfileWhatsappStatus ||
        autoUpdateProfileYoutube != origAutoUpdateProfileYoutube ||
        uploadCategoryId != origUploadCategoryId

    // Load user data
    LaunchedEffect(Unit) {
        viewModel.loadUser(null)
        when (val r = appRepository.getCategories()) {
            is DomainResult.Success -> categories = r.data
            else -> {}
        }
    }

    // Populate fields from user
    LaunchedEffect(userState) {
        if (userState is UiState.Success) {
            val user = (userState as UiState.Success).data
            instagramId = user.instagramId ?: ""
            whatsappStatusId = user.whatsappStatusId ?: ""
            youtubeChannelId = user.youtubeChannelId ?: ""
            autoPullInstagram = user.autoPullInstagram
            autoPullWhatsappStatus = user.autoPullWhatsappStatus
            autoPullYoutube = user.autoPullYoutube
            autoUpdateProfileInstagram = user.autoUpdateProfileInstagram
            autoUpdateProfileWhatsappStatus = user.autoUpdateProfileWhatsappStatus
            autoUpdateProfileYoutube = user.autoUpdateProfileYoutube
            uploadCategoryId = user.uploadCategoryId
            // Set originals
            origInstagramId = user.instagramId ?: ""
            origWhatsappStatusId = user.whatsappStatusId ?: ""
            origYoutubeChannelId = user.youtubeChannelId ?: ""
            origAutoPullInstagram = user.autoPullInstagram
            origAutoPullWhatsappStatus = user.autoPullWhatsappStatus
            origAutoPullYoutube = user.autoPullYoutube
            origAutoUpdateProfileInstagram = user.autoUpdateProfileInstagram
            origAutoUpdateProfileWhatsappStatus = user.autoUpdateProfileWhatsappStatus
            origAutoUpdateProfileYoutube = user.autoUpdateProfileYoutube
            origUploadCategoryId = user.uploadCategoryId
        }
    }

    // Handle save result
    LaunchedEffect(updateProfileState) {
        when (updateProfileState) {
            is UiState.Success -> {
                viewModel.resetUpdateProfileState()
                onNavigateBack()
            }
            is UiState.Error -> {
                scope.launch {
                    snackbarHostState.showSnackbar(
                        (updateProfileState as UiState.Error).message
                    )
                }
                viewModel.resetUpdateProfileState()
            }
            else -> {}
        }
    }

    DisposableEffect(Unit) {
        onDispose { viewModel.resetUpdateProfileState() }
    }

    ScreenScaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TovTalkTopBar(
                title = StringProvider.get("settings_content_upload_management"),
                onBack = onNavigateBack,
            )
        },
        constrainContentWidth = false,
    ) { paddingValues ->
        val contentUploadContent = @Composable {
        when (userState) {
            is UiState.Loading, is UiState.Idle, is UiState.KashrutBlocked -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = TovTalkTheme.colors.brand)
                }
            }
            is UiState.Error -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentAlignment = Alignment.Center
                ) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text(
                            (userState as UiState.Error).message,
                            color = TovTalkTheme.colors.error,
                            style = TovTalkTheme.typography.bodyMedium
                        )
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
                        TextButton(onClick = { viewModel.loadUser(null) }) {
                            Text(StringProvider.get("common_retry"), color = TovTalkTheme.colors.brand)
                        }
                    }
                }
            }
            is UiState.Success -> {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(paddingValues)
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = TovTalkTheme.spacing.screenPadding),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))

                    // === Content Sources Section ===
                    ContentSection(title = StringProvider.get("settings_content_sources")) {
                        SourceSettingsCard(
                            icon = Icons.Default.CameraAlt,
                            label = StringProvider.get("edit_profile_auto_pull_instagram").substringBefore(" ").ifBlank { "אינסטגרם" },
                            isExpanded = expandedSource == ContentSource.INSTAGRAM,
                            onToggleExpand = {
                                expandedSource = if (expandedSource == ContentSource.INSTAGRAM) null else ContentSource.INSTAGRAM
                            }
                        ) {
                            SourceFields(
                                value = instagramId,
                                onValueChange = {
                                    instagramId = it
                                    if (it.isNotBlank()) {
                                        autoPullInstagram = true
                                        autoPullWhatsappStatus = false
                                        autoPullYoutube = false
                                    }
                                },
                                label = StringProvider.get("edit_profile_instagram_id"),
                                autoPull = autoPullInstagram,
                                onAutoPullChange = {
                                    autoPullInstagram = it
                                    if (it) { autoPullWhatsappStatus = false; autoPullYoutube = false }
                                },
                                autoUpdateProfile = autoUpdateProfileInstagram,
                                onAutoUpdateProfileChange = { autoUpdateProfileInstagram = it },
                                autoPullLabel = StringProvider.get("edit_profile_auto_pull_instagram"),
                                autoUpdateLabel = StringProvider.get("edit_profile_auto_update_profile_instagram")
                            )
                        }
                        HorizontalDivider(
                            modifier = Modifier.padding(start = TovTalkTheme.spacing.screenPadding + 36.dp),
                            thickness = 0.5.dp,
                            color = TovTalkTheme.colors.border
                        )
                        SourceSettingsCard(
                            icon = Icons.Default.ChatBubble,
                            label = StringProvider.get("edit_profile_auto_pull_whatsapp").substringBefore(" ").ifBlank { "וואטסאפ" },
                            isExpanded = expandedSource == ContentSource.WHATSAPP,
                            onToggleExpand = {
                                expandedSource = if (expandedSource == ContentSource.WHATSAPP) null else ContentSource.WHATSAPP
                            }
                        ) {
                            SourceFields(
                                value = whatsappStatusId,
                                onValueChange = {
                                    whatsappStatusId = it
                                    if (it.isNotBlank()) {
                                        autoPullWhatsappStatus = true
                                        autoPullInstagram = false
                                        autoPullYoutube = false
                                    }
                                },
                                label = StringProvider.get("edit_profile_whatsapp_status_id"),
                                autoPull = autoPullWhatsappStatus,
                                onAutoPullChange = {
                                    autoPullWhatsappStatus = it
                                    if (it) { autoPullInstagram = false; autoPullYoutube = false }
                                },
                                autoUpdateProfile = autoUpdateProfileWhatsappStatus,
                                onAutoUpdateProfileChange = { autoUpdateProfileWhatsappStatus = it },
                                autoPullLabel = StringProvider.get("edit_profile_auto_pull_whatsapp"),
                                autoUpdateLabel = StringProvider.get("edit_profile_auto_update_profile_whatsapp")
                            )
                            HorizontalDivider(
                                modifier = Modifier.padding(start = TovTalkTheme.spacing.screenPadding + 36.dp),
                                thickness = 0.5.dp,
                                color = TovTalkTheme.colors.border
                            )
                            Text(
                                StringProvider.get("edit_profile_contacts_whatsapp_message"),
                                style = TovTalkTheme.typography.bodySmall,
                                color = TovTalkTheme.colors.textSecondary,
                                modifier = Modifier.fillMaxWidth().padding(horizontal = TovTalkTheme.spacing.md)
                            )
                            TextButton(onClick = { showContactsDialog = true }) {
                                Text(
                                    StringProvider.get("edit_profile_contacts_save_button"),
                                    color = TovTalkTheme.colors.brand
                                )
                            }
                        }
                        HorizontalDivider(
                            modifier = Modifier.padding(start = TovTalkTheme.spacing.screenPadding + 36.dp),
                            thickness = 0.5.dp,
                            color = TovTalkTheme.colors.border
                        )
                        SourceSettingsCard(
                            icon = Icons.Default.VideoLibrary,
                            label = StringProvider.get("edit_profile_auto_pull_youtube").substringBefore(" ").ifBlank { "יוטיוב" },
                            isExpanded = expandedSource == ContentSource.YOUTUBE,
                            onToggleExpand = {
                                expandedSource = if (expandedSource == ContentSource.YOUTUBE) null else ContentSource.YOUTUBE
                            }
                        ) {
                            SourceFields(
                                value = youtubeChannelId,
                                onValueChange = {
                                    youtubeChannelId = it
                                    if (it.isNotBlank()) {
                                        autoPullYoutube = true
                                        autoPullInstagram = false
                                        autoPullWhatsappStatus = false
                                    }
                                },
                                label = StringProvider.get("edit_profile_youtube_channel_id"),
                                autoPull = autoPullYoutube,
                                onAutoPullChange = {
                                    autoPullYoutube = it
                                    if (it) { autoPullInstagram = false; autoPullWhatsappStatus = false }
                                },
                                autoUpdateProfile = autoUpdateProfileYoutube,
                                onAutoUpdateProfileChange = { autoUpdateProfileYoutube = it },
                                autoPullLabel = StringProvider.get("edit_profile_auto_pull_youtube"),
                                autoUpdateLabel = StringProvider.get("edit_profile_auto_update_profile_youtube")
                            )
                        }
                    }

                    // === Upload Category Section ===
                    if (categories.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
                        ContentSection(title = StringProvider.get("settings_upload_category")) {
                            val sortedCategories = categories.sortedBy { it.sortOrder }
                            val selectedName = uploadCategoryId?.let { id ->
                                sortedCategories.find { it.id == id }?.name
                            } ?: (StringProvider.get("common_none") + " / " + StringProvider.get("common_clear"))

                            Box(modifier = Modifier.fillMaxWidth().padding(TovTalkTheme.spacing.md)) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clip(RoundedCornerShape(TovTalkTheme.radius.sm))
                                        .background(TovTalkTheme.colors.surfaceOverlay)
                                        .clickable { categoryDropdownExpanded = true }
                                        .padding(TovTalkTheme.spacing.md),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Text(
                                        text = selectedName,
                                        style = TovTalkTheme.typography.bodyMedium,
                                        color = TovTalkTheme.colors.textPrimary
                                    )
                                    Icon(
                                        imageVector = Icons.Default.ArrowDropDown,
                                        contentDescription = null,
                                        tint = TovTalkTheme.colors.textSecondary
                                    )
                                }
                                DropdownMenu(
                                    expanded = categoryDropdownExpanded,
                                    onDismissRequest = { categoryDropdownExpanded = false }
                                ) {
                                    DropdownMenuItem(
                                        text = {
                                            Text(
                                                StringProvider.get("common_none") + " / " + StringProvider.get("common_clear"),
                                                style = TovTalkTheme.typography.bodyMedium
                                            )
                                        },
                                        onClick = {
                                            uploadCategoryId = null
                                            categoryDropdownExpanded = false
                                        }
                                    )
                                    sortedCategories.forEach { category ->
                                        DropdownMenuItem(
                                            text = { Text(category.name, style = TovTalkTheme.typography.bodyMedium) },
                                            onClick = {
                                                uploadCategoryId = category.id
                                                categoryDropdownExpanded = false
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }

                    // === Save Button ===
                    Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))
                    AnimatedVisibility(
                        visible = hasChanges,
                        enter = fadeIn(),
                        exit = fadeOut()
                    ) {
                        val isSaving = updateProfileState is UiState.Loading
                        Box(
                            modifier = Modifier
                                .shadow(
                                    elevation = TovTalkTheme.elevation.level3,
                                    shape = RoundedCornerShape(TovTalkTheme.radius.xl)
                                )
                                .clip(RoundedCornerShape(TovTalkTheme.radius.xl))
                                .background(TovTalkTheme.colors.brand)
                                .clickable(enabled = !isSaving) {
                                    viewModel.updateProfile(
                                        instagramId = if (autoPullInstagram) instagramId else "",
                                        whatsappStatusId = if (autoPullWhatsappStatus) whatsappStatusId else "",
                                        youtubeChannelId = if (autoPullYoutube) youtubeChannelId else "",
                                        autoPullInstagram = autoPullInstagram,
                                        autoPullWhatsappStatus = autoPullWhatsappStatus,
                                        autoPullYoutube = autoPullYoutube,
                                        autoUpdateProfileInstagram = if (autoPullInstagram) autoUpdateProfileInstagram else false,
                                        autoUpdateProfileWhatsappStatus = if (autoPullWhatsappStatus) autoUpdateProfileWhatsappStatus else false,
                                        autoUpdateProfileYoutube = if (autoPullYoutube) autoUpdateProfileYoutube else false,
                                        uploadCategoryId = uploadCategoryId
                                    )
                                }
                                .padding(horizontal = TovTalkTheme.spacing.xl, vertical = TovTalkTheme.spacing.xs),
                            contentAlignment = Alignment.Center
                        ) {
                            if (isSaving) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(TovTalkTheme.sizes.iconSm),
                                    color = TovTalkTheme.colors.onBrand,
                                    strokeWidth = TovTalkTheme.spacing.xxxs
                                )
                            } else {
                                Text(
                                    StringProvider.get("edit_profile_save_changes"),
                                    color = TovTalkTheme.colors.onBrand,
                                    style = TovTalkTheme.typography.titleSmall,
                                    fontWeight = FontWeight.SemiBold
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xxl))
                }
            }
        }
        }
        CenteredContentWrapper { contentUploadContent() }
    }

    if (showContactsDialog) {
        EditProfileContactsDialog(onDismiss = { showContactsDialog = false })
    }
}

// === Helper Composables ===

@Composable
private fun ContentSection(
    title: String,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = title,
            style = TovTalkTheme.typography.labelMedium,
            color = TovTalkTheme.colors.textSecondary,
            fontWeight = FontWeight.Medium,
            modifier = Modifier.padding(
                start = TovTalkTheme.spacing.md,
                top = 20.dp,
                bottom = 6.dp
            )
        )
        content()
    }
}

@Composable
private fun SourceSettingsCard(
    icon: ImageVector,
    label: String,
    isExpanded: Boolean,
    onToggleExpand: () -> Unit,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(onClick = onToggleExpand)
                .padding(horizontal = TovTalkTheme.spacing.screenPadding, vertical = 13.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                imageVector = icon,
                contentDescription = label,
                tint = TovTalkTheme.colors.textTertiary,
                modifier = Modifier.size(22.dp)
            )
            Spacer(modifier = Modifier.width(14.dp))
            Text(
                text = label,
                style = TovTalkTheme.typography.bodyLarge,
                color = TovTalkTheme.colors.textPrimary,
                modifier = Modifier.weight(1f)
            )
            Icon(
                imageVector = if (isExpanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                contentDescription = null,
                tint = TovTalkTheme.colors.textTertiary,
                modifier = Modifier.size(20.dp)
            )
        }
        AnimatedVisibility(visible = isExpanded) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(
                        start = TovTalkTheme.spacing.screenPadding + 36.dp,
                        end = TovTalkTheme.spacing.screenPadding,
                        bottom = TovTalkTheme.spacing.md
                    ),
                verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.sm)
            ) {
                content()
            }
        }
    }
}

@Composable
private fun SourceFields(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    autoPull: Boolean,
    onAutoPullChange: (Boolean) -> Unit,
    autoUpdateProfile: Boolean,
    onAutoUpdateProfileChange: (Boolean) -> Unit,
    autoPullLabel: String,
    autoUpdateLabel: String
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label, style = TovTalkTheme.typography.bodyMedium) },
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        colors = TextFieldDefaults.colors(
            focusedTextColor = TovTalkTheme.colors.textPrimary,
            unfocusedTextColor = TovTalkTheme.colors.textPrimary,
            focusedContainerColor = TovTalkTheme.colors.surfaceOverlay,
            unfocusedContainerColor = TovTalkTheme.colors.surfaceOverlay,
            focusedIndicatorColor = TovTalkTheme.colors.brand,
            unfocusedIndicatorColor = TovTalkTheme.colors.border,
            cursorColor = TovTalkTheme.colors.brand,
            focusedLabelColor = TovTalkTheme.colors.brand,
            unfocusedLabelColor = TovTalkTheme.colors.textSecondary
        )
    )
    ToggleRow(
        label = autoPullLabel,
        checked = autoPull,
        onCheckedChange = onAutoPullChange
    )
    ToggleRow(
        label = autoUpdateLabel,
        checked = autoUpdateProfile,
        onCheckedChange = onAutoUpdateProfileChange
    )
}

@Composable
private fun ToggleRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onCheckedChange(!checked) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = label,
            style = TovTalkTheme.typography.bodyMedium,
            color = TovTalkTheme.colors.textPrimary,
            modifier = Modifier.weight(1f)
        )
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = TovTalkTheme.colors.brand,
                checkedTrackColor = TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.half)
            )
        )
    }
}
